"use client";

import { cn } from "@/lib/utils";
import { FC, useMemo, useState, useEffect, useRef } from "react";
import { z } from "zod";
import Image from "next/image";
import { LeftArrow, RightArrow } from "@/assets/Arrow";
import { ProgressSlider } from "@/components/atoms/ProgressSlider";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { leadCtaClick } from "@/customAnalytics/trackingEvents";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";

const CardSchema = z.object({
    id: z.union([z.number(), z.string()]),
    meta_image: z.string(),
    meta_alt_image: z.string().optional().default(""),
    meta_logo_image: z.string().optional().default(""),
    meta_title: z.string().optional().default(""),
    meta_title_color: z.string().optional().default("#000000"),
    meta_sub_title: z.string().optional().default(""),
    meta_sub_title_color: z.string().optional().default("rgba(0,0,0,0.8)"),
    meta_priority: z.coerce.number().optional().default(0),
});

const CaseStudy1DataSchema = z.object({
    desktop_background_color: z.string().optional().default(""),
    mobile_background_color: z.string().optional().default(""),
    backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
    gradient_color_1: z.string().optional().default(""),
    gradient_color_2: z.string().optional().default(""),
    gradient_color_3: z.string().optional().default(""),
    gradient_color_4: z.string().optional().default(""),
    title: z.string().optional().default("Solve actual wealth"),
    title_color: z.string().optional().default("#222222"),
    title_highlight: z.string().optional().default("management challenges"),
    title_highlight_color: z.string().optional().default("#342B7C"),
    font_style: z.string().optional().default("var(--font-merriweather)"),
    description: z.string().optional().default(""),
    description_color: z.string().optional().default("rgba(0,0,0,0.7)"),
    button_text: z.string().optional().default("Apply Now"),
    button_start_color: z.string().optional().default(""),
    button_end_color: z.string().optional().default(""),
    button_gradient_direction: z.string().optional().default(""),
    button_text_color: z.string().optional().default("#FFFFFF"),
    left_navigation_icon: z.string().optional(),
    alt_text_left_nav_icon: z.string().optional().default(""),
    right_navigation_icon: z.string().optional(),
    alt_text_right_nav_icon: z.string().optional().default(""),
    /* in milliseconds (0 = no auto-scroll) */
    scroll_frequency: z.coerce.number().optional().default(0),
    /** MySQL / API may send 0/1; match ProgramBenefits1 truthy handling. */
    enable_animation: z
        .preprocess((v) => v === true || v === 1 || v === "1", z.boolean())
        .optional()
        .default(false),
    metadata: z.array(CardSchema).optional().default([]),
});

type CaseStudyCard = z.infer<typeof CardSchema>;

// Case Study Card Component
const CaseStudyCard: FC<{
    card: CaseStudyCard;
    isMobile: boolean;
    position?: number;
    priority?: boolean;
}> = ({ card, isMobile, position = 0, priority = false }) => {
    const mobileStyle = isMobile
        ? {
              transform: `translateX(${position * 100}%)`,
              opacity: position === 0 ? 1 : 0,
              zIndex: position === 0 ? 10 : 0,
          }
        : undefined;

    return (
        <div
            className={cn(
                "shrink-0 overflow-hidden",
                isMobile
                    ? "absolute inset-0 transition-all duration-500 ease-in-out w-full"
                    : "relative",
                // Card widths
                "w-full",
                "sm:w-[300px]",
                "md:w-[340px]",
                "lg:w-[400px]",
                "xl:w-[400px]"
            )}
            style={mobileStyle}
        >
            {/* Image area with gradient + blur overlay */}
            <div
                className={cn(
                    "relative w-full overflow-hidden",
                    // Image heights
                    "h-[200px]",
                    "sm:h-[165px]",
                    "md:h-[180px]",
                    "lg:h-[219px]",
                    "xl:h-[219px]"
                )}
            >
                <Image
                    src={card.meta_image}
                    alt={card.meta_alt_image || card.meta_title}
                    fill
                    className="object-cover"
                    priority={priority}
                />

                {/* Gradient + blur overlay */}
                <div
                    className={cn(
                        "absolute inset-0 flex items-center justify-center",
                        "backdrop-blur-[10.6px]",
                        "sm:backdrop-blur-[9px]",
                        "md:backdrop-blur-[10.25px]",
                        "lg:backdrop-blur-[12px]",
                        "xl:backdrop-blur-[12px]"
                    )}
                    style={{
                        backgroundImage:
                            "linear-gradient(to bottom, rgba(52,43,124,0.3), rgba(0,0,0,0.3))",
                    }}
                >
                    {card.meta_logo_image ? (
                        <div
                            className={cn(
                                "relative",
                                "w-[170px] h-[45px]",
                                "sm:w-[137px] sm:h-[39px]",
                                "md:w-[150px] md:h-[43px]",
                                "lg:w-[175px] lg:h-[50px]",
                                "xl:w-[175px] xl:h-[50px]"
                            )}
                        >
                            <Image
                                src={card.meta_logo_image}
                                alt={card.meta_alt_image || card.meta_title}
                                fill
                                className="object-contain"
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Footer */}
            <div
                className={cn(
                    "flex flex-col",
                    "p-[10px] gap-[10px]",
                    "sm:p-[15px] sm:gap-[8px]",
                    "md:p-[18px] md:gap-[8px]",
                    "lg:p-[20px] lg:gap-[10px]",
                    "xl:p-[20px] xl:gap-[10px]"
                )}
                style={{ backgroundColor: "rgba(2,167,182,0.2)" }}
            >
                <p
                    className={cn(
                        "font-semibold leading-[1.4] line-clamp-2",
                        "text-[15px] tracking-[0.15px]",
                        "sm:text-[13.5px] sm:tracking-[0.135px]",
                        "md:text-[16px] md:tracking-[0.16px]",
                        "lg:text-[18px] lg:tracking-[0.18px]",
                        "xl:text-[18px] xl:tracking-[0.18px]"
                    )}
                    style={{
                        fontFamily: "var(--font-google-sans)",
                        color: card.meta_title_color,
                    }}
                >
                    {card.meta_title}
                </p>
                <p
                    className={cn(
                        "font-normal leading-[1.4] line-clamp-3",
                        "text-[14px] tracking-[0.14px]",
                        "sm:text-[12px] sm:tracking-[0.12px]",
                        "md:text-[14px] md:tracking-[0.14px]",
                        "lg:text-[16px] lg:tracking-[0.16px]",
                        "xl:text-[16px] xl:tracking-[0.16px]"
                    )}
                    style={{
                        fontFamily: "var(--font-google-sans)",
                        color: card.meta_sub_title_color,
                    }}
                >
                    {card.meta_sub_title}
                </p>
            </div>
        </div>
    );
};

export const CaseStudy1: FC<any> = (props) => {
    const actualData = props?.data?.data || props?.data || props;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [isPaused, setIsPaused] = useState(false);

    const sectionRef = useRef<HTMLElement>(null);
    const dispatch = useDispatch();

    const parsedResult = useMemo(
        () => CaseStudy1DataSchema.safeParse(actualData),
        [actualData]
    );

    const parsedData = useMemo(
        () => (parsedResult.success ? parsedResult.data : null),
        [parsedResult]
    );

    const cards = useMemo(
        () =>
            parsedData
                ? [...parsedData.metadata].sort(
                      (a, b) => a.meta_priority - b.meta_priority
                  )
                : [],
        [parsedData]
    );

    const ms = parsedData?.scroll_frequency || 0;
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Detect mobile/desktop (< sm breakpoint = 1080)
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1080);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Track widget viewed
    // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
    // useEffect(() => {
    //     if (sectionRef.current) {
    //         FireWidgetViewedEvent(sectionRef, "case_study");
    //     }
    // }, []);

    // Auto-scroll carousel based on scroll_frequency
    useEffect(() => {
        if (cards.length <= 1 || isPaused || ms <= 0) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                if (prev >= cards.length - 1) {
                    return 0;
                }
                return prev + 1;
            });
        }, ms);

        return () => clearInterval(interval);
    }, [ms, cards.length, isPaused]);

    if (!parsedResult.success || !parsedData) {
        console.error("validation failed", parsedResult.error);
        return null;
    }

    if (cards.length === 0) {
        return null;
    }

    const isAnimationEnabled = parsedData.enable_animation;

    const handlePrev = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setCurrentIndex((prev) => Math.max(0, prev - 1));
        setIsPaused(true);
        timeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
    };

    const handleNext = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setCurrentIndex((prev) => Math.min(cards.length - 1, prev + 1));
        setIsPaused(true);
        timeoutRef.current = setTimeout(() => setIsPaused(false), 3000);
    };

    // Touch swipe handlers
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > minSwipeDistance) {
            handleNext();
        } else if (distance < -minSwipeDistance) {
            handlePrev();
        }
    };

    const bgColor = formatBackgroundStyle(parsedData, isMobile, "#ffffff");

    const handleCtaClick = () => {
        leadCtaClick({
            section: "case_study",
            component: "cta_button",
            label: parsedData.button_text,
        });
        dispatch(
            handleOpenLeaddrop({
                variant: LEAD_DROP_VARIANTS.REGISTER,
            })
        );
    };

    const ctaBackgroundImage = `linear-gradient(${
        parsedData.button_gradient_direction || "0deg"
    }, ${parsedData.button_start_color || "#342b7c"}, ${
        parsedData.button_end_color || "#02a7b6"
    })`;

    // Card width + gap per breakpoint, used to translate the desktop track.
    // Built as a CSS calc so it tracks the active Tailwind breakpoint at runtime.
    const trackTransform = `translateX(calc(-1 * ${currentIndex} * (var(--cs-card-w) + var(--cs-card-gap))))`;

    // Heading block (shared by mobile + desktop)
    const HeadingBlock = (
        <div
            className={cn(
                "flex flex-col",
                "gap-[12px]",
                "md:gap-[13.667px]",
                "lg:gap-[16px]",
                "xl:gap-[16px]"
            )}
        >
         
                <div className="flex flex-col leading-[1.4]">
                    <span
                        className={cn(
                            "font-semibold leading-[1.4]",
                            "text-[24px] tracking-[0.24px]",
                            "sm:text-[24px] sm:tracking-[0.24px]",
                            "md:text-[26px] md:tracking-[0.26px]",
                            "lg:text-[30px] lg:tracking-[0.3px]",
                            "xl:text-[30px] xl:tracking-[0.3px]"
                        )}
                        style={{
                            fontFamily: "var(--font-google-sans)",
                            color: parsedData.title_color,
                        }}
                    >
                        {parsedData.title}
                    </span>
                    <span
                        className={cn(
                            "font-extrabold italic leading-[1.4]",
                            "text-[24px]",
                            "sm:text-[24px]",
                            "md:text-[26px]",
                            "lg:text-[28px]",
                            "xl:text-[28px]"
                        )}
                        style={{
                            fontFamily: parsedData.font_style,
                            color: parsedData.title_highlight_color,
                        }}
                    >
                        {parsedData.title_highlight}
                    </span>
                </div>
        

            {parsedData.description ? (
                <RevealOnScroll y={30} enableAnimation={isAnimationEnabled}>
                    <p
                        className={cn(
                            "leading-[1.49]",
                            "text-[14px]",
                            "sm:text-[14px]",
                            "md:text-[15px]",
                            "lg:text-[18px]",
                            "xl:text-[18px]"
                        )}
                        style={{
                            fontFamily: "var(--font-google-sans)",
                            color: parsedData.description_color,
                        }}
                    >
                        {parsedData.description}
                    </p>
                </RevealOnScroll>
            ) : null}
        </div>
    );

    // Controller (arrows + progress slider)
    const Controller =
        cards.length > 1 ? (
            <RevealOnScroll enableAnimation={isAnimationEnabled}>
            <div className={cn("flex items-center shrink-0 w-full gap-[20px]")}>
                {/* Previous Button */}
                {parsedData.left_navigation_icon ? (
                    <button
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        className={cn(
                            "shrink-0 transition-all",
                            "w-[44px] h-[44px] sm:w-[33px] sm:h-[33px] md:w-[37px] md:h-[37px] lg:w-[44px] lg:h-[44px] xl:w-[44px] xl:h-[44px]",
                            currentIndex === 0
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer hover:opacity-80"
                        )}
                        aria-label="Previous case study"
                    >
                        <Image
                            src={parsedData.left_navigation_icon}
                            alt={parsedData.alt_text_left_nav_icon || "Previous"}
                            width={44}
                            height={44}
                            className="w-full h-full object-contain"
                        />
                    </button>
                ) : (
                    <button
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                        className={cn(
                            "flex items-center justify-center rounded-full border transition-all shrink-0",
                            "p-[10px] sm:p-[7.5px] md:p-[8.5px] lg:p-[10px] xl:p-[10px]",
                            currentIndex === 0
                                ? "border-gray-300 text-gray-300 cursor-not-allowed"
                                : "border-black text-black hover:bg-gray-100 cursor-pointer"
                        )}
                        aria-label="Previous case study"
                    >
                        <LeftArrow
                            className={cn(
                                "w-[24px] h-[24px] sm:w-[18px] sm:h-[18px] md:w-[20.5px] md:h-[20.5px] lg:w-[24px] lg:h-[24px] xl:w-[24px] xl:h-[24px]"
                            )}
                        />
                    </button>
                )}

                <ProgressSlider
                    currentIndex={currentIndex}
                    totalCount={cards.length}
                    barWidthPx={61}
                    trackClassName="flex-1"
                />

                {/* Next Button */}
                {parsedData.right_navigation_icon ? (
                    <button
                        onClick={handleNext}
                        disabled={currentIndex === cards.length - 1}
                        className={cn(
                            "shrink-0 transition-all",
                            "w-[44px] h-[44px] sm:w-[33px] sm:h-[33px] md:w-[37px] md:h-[37px] lg:w-[44px] lg:h-[44px] xl:w-[44px] xl:h-[44px]",
                            currentIndex === cards.length - 1
                                ? "opacity-50 cursor-not-allowed"
                                : "cursor-pointer hover:opacity-80"
                        )}
                        aria-label="Next case study"
                    >
                        <Image
                            src={parsedData.right_navigation_icon}
                            alt={parsedData.alt_text_right_nav_icon || "Next"}
                            width={44}
                            height={44}
                            className="w-full h-full object-contain"
                        />
                    </button>
                ) : (
                    <button
                        onClick={handleNext}
                        disabled={currentIndex === cards.length - 1}
                        className={cn(
                            "flex items-center justify-center rounded-full border transition-all shrink-0",
                            "p-[10px] sm:p-[7.5px] md:p-[8.5px] lg:p-[10px] xl:p-[10px]",
                            currentIndex === cards.length - 1
                                ? "border-gray-300 text-gray-300 cursor-not-allowed"
                                : "border-black text-black hover:bg-gray-100 cursor-pointer"
                        )}
                        aria-label="Next case study"
                    >
                        <RightArrow
                            className={cn(
                                "w-[24px] h-[24px] sm:w-[18px] sm:h-[18px] md:w-[20.5px] md:h-[20.5px] lg:w-[24px] lg:h-[24px] xl:w-[24px] xl:h-[24px]"
                            )}
                        />
                    </button>
                )}
            </div>
            </RevealOnScroll>
        ) : null;

    // CTA button
    const CtaButton = parsedData.button_text ? (
        <RevealOnScroll enableAnimation={isAnimationEnabled}>
        <button
            type="button"
            data-cta-name="apply_now"
            onClick={handleCtaClick}
            className={cn(
                "btn-base w-full sm:w-auto text-[16px]",
                "px-[24px] py-[16px]",
                "sm:px-[20px] sm:py-[14px]"
            )}
            style={{
                fontFamily: "var(--font-google-sans)",
                backgroundImage: ctaBackgroundImage,
                color: parsedData.button_text_color,
            }}
        >
            {parsedData.button_text}
        </button>
        </RevealOnScroll>
    ) : null;

    return (
        <section
            ref={sectionRef}
            className={cn("relative w-full overflow-hidden")}
            style={bgColor}
        >
            {/* DESKTOP (sm and up) — full-bleed row: left padding only, no right
                padding and no right max-width, so the card track overflows the
                right edge of the viewport (cards are clipped at the screen edge). */}
            <div
                className={cn(
                    "hidden sm:flex flex-row items-stretch",
                    "sm:pl-[60px] sm:py-[40px]",
                    "md:pl-[80px] md:py-[60px]",
                    "lg:pl-[80px] lg:py-[60px]",
                    "xl:pl-[140px] xl:py-[80px]",
                    "sm:gap-[40px]",
                    "md:gap-[50px]",
                    "lg:gap-[60px]",
                    "xl:gap-[60px]"
                )}
            >
                {/* LEFT column */}
                <div
                    className={cn(
                        "flex flex-col justify-between shrink-0",
                        "sm:w-[327px]",
                        "md:w-[380px]",
                        "lg:w-[437px]",
                        "xl:w-[437px]",
                        "sm:gap-[45px]",
                        "md:gap-[50px]",
                        "lg:gap-[60px]",
                        "xl:gap-[60px]"
                    )}
                >
                    {HeadingBlock}
                    {Controller}
                </div>

                {/* RIGHT column — horizontal track; overflows the viewport on the right */}
                <div className="flex-1 min-w-0 overflow-hidden">
                    <div
                        className={cn(
                            "flex transition-transform duration-500 ease-in-out",
                            "[--cs-card-w:300px] [--cs-card-gap:15px] gap-[15px]",
                            "md:[--cs-card-w:340px] md:[--cs-card-gap:20px] md:gap-[20px]",
                            "lg:[--cs-card-w:400px] lg:[--cs-card-gap:20px] lg:gap-[20px]",
                            "xl:[--cs-card-w:400px] xl:[--cs-card-gap:20px] xl:gap-[20px]"
                        )}
                        style={{ transform: trackTransform }}
                    >
                        {cards.map((card, index) => (
                            <CaseStudyCard
                                key={card.id}
                                card={card}
                                isMobile={false}
                                priority={index === 0}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA centered across the viewport at the bottom (desktop only) */}
            {CtaButton ? (
                <div
                    className={cn(
                        "hidden sm:flex justify-center w-full",
                        "sm:mt-[40px] sm:pb-[40px]",
                        "md:mt-[36px] md:pb-[60px]",
                        "lg:mt-[40px] lg:pb-[60px]",
                        "xl:mt-[40px] xl:pb-[80px]"
                    )}
                >
                    {CtaButton}
                </div>
            ) : null}

            {/* MOBILE (xs) */}
            <div className="flex flex-col sm:hidden gap-[24px] px-[20px] py-[40px]">
                {HeadingBlock}

                {/* Single card with index-driven translate */}
                <div
                    className="relative w-full min-h-[320px]"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {cards.map((card, index) => (
                        <CaseStudyCard
                            key={card.id}
                            card={card}
                            isMobile={true}
                            position={index - currentIndex}
                            priority={index === 0}
                        />
                    ))}
                </div>

                {Controller}

                {CtaButton ? (
                    <div className="flex justify-center w-full">{CtaButton}</div>
                ) : null}
            </div>
        </section>
    );
};

export default CaseStudy1;
